// -----------------------------
// Constants
// -----------------------------
const WEEKDAY_MAP = {
  SUN: "Sunday",
  MON: "Monday",
  TUE: "Tuesday",
  WED: "Wednesday",
  THU: "Thursday",
  FRI: "Friday",
  SAT: "Saturday"
};

// -----------------------------
// Field Parser
// -----------------------------
function parseField(field, min, max, pad = true) {
  if (field === "*") {
    return { type: "all" };
  }

  // Range with step: 18-26/2
  if (field.includes("-") && field.includes("/")) {
    const [range, step] = field.split("/");
    const [start, end] = range.split("-");
    return {
      type: "range_step",
      start,
      end,
      step
    };
  }

  // Step: */2, 1/5
  if (field.includes("/")) {
    const [base, step] = field.split("/");
    return {
      type: "step",
      base: base === "*" ? `${min}-${max}` : base,
      step
    };
  }

  // Range: 9-17
  if (field.includes("-")) {
    const [start, end] = field.split("-");
    return { type: "range", start, end };
  }

  // List: 1,3,5
  if (field.includes(",")) {
    return {
      type: "list",
      values: field.split(",").map(v =>
        pad ? v.padStart(2, "0") : v
      )
    };
  }

  // Single value
  return {
    type: "single",
    value: pad ? field.padStart(2, "0") : field
  };
}

// -----------------------------
// Weekday Parser
// -----------------------------
function parseWeekdays(field) {
  if (field === "*") return "every day";

  if (field.includes(",")) {
    return field
      .split(",")
      .map(d => WEEKDAY_MAP[d] || d)
      .join(", ");
  }

  if (field.includes("-")) {
    const [start, end] = field.split("-");
    return `${WEEKDAY_MAP[start]} to ${WEEKDAY_MAP[end]}`;
  }

  return WEEKDAY_MAP[field] || field;
}

// -----------------------------
// Main Converter
// -----------------------------
function awsCronToHumanReadable(cron) {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 6) {
    throw new Error("Invalid AWS cron expression");
  }

  const [minute, hour, dom, month, dow] = parts;

  const minuteParsed = parseField(minute, 0, 59);
  const hourParsed = parseField(hour, 0, 23);
  const domParsed = parseField(dom, 1, 31, false);
  const monthParsed = parseField(month, 1, 12, false);

  // -----------------------------
  // Time Formatting
  // -----------------------------
  let timePart = "";

  if (minuteParsed.type === "single" && hourParsed.type === "single") {
    timePart = `At ${hourParsed.value}:${minuteParsed.value}`;
  }

  else if (hourParsed.type === "list" && minuteParsed.type === "single") {
    timePart = `At ${hourParsed.values
      .map(h => `${h}:${minuteParsed.value}`)
      .join(", ")}`;
  }

  else if (hourParsed.type === "range") {
    const minSuffix = minuteParsed.type === "single" ? ` at minute ${minuteParsed.value}` : '';
    timePart = `Every hour from ${hourParsed.start}:00 to ${hourParsed.end}:00${minSuffix}`;
  }

  else if (hourParsed.type === "step") {
    const minSuffix = minuteParsed.type === "single" ? ` at minute ${minuteParsed.value}` : '';
    timePart = `Every ${hourParsed.step} hours${minSuffix}`;
  }

  else if (minuteParsed.type === "step") {
    timePart = `Every ${minuteParsed.step} minutes`;
  }

  else {
    timePart = "On a custom time schedule";
  }

  // -----------------------------
  // Date Formatting
  // -----------------------------
  let datePart = "";

  // Day-of-month + month step (e.g. 1 */7)
  if (domParsed.type === "single" && monthParsed.type === "step" && dow === "?") {
    datePart = `on day ${domParsed.value} every ${monthParsed.step} months`;
  }

  // Day-of-month range + step (18-26/2)
  else if (domParsed.type === "range_step" && dow === "?") {
    datePart = `every ${domParsed.step} days from day ${domParsed.start} to ${domParsed.end} of every month`;
  }

  // Day-of-month range
  else if (domParsed.type === "range" && dow === "?") {
    datePart = `from day ${domParsed.start} to ${domParsed.end} of every month`;
  }

  // Daily
  else if (dom === "*" && dow === "?") {
    datePart = "daily";
  }

  // Specific day each month
  else if (domParsed.type === "single" && month === "*" && dow === "?") {
    datePart = `on day ${domParsed.value} of every month`;
  }

  // Weekdays
  else if (dow !== "?" && dom === "*") {
    datePart = `on ${parseWeekdays(dow)}`;
  }

  else {
    datePart = "on a custom date schedule";
  }

  return `${timePart} ${datePart}`;
}

export { awsCronToHumanReadable };